import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { API_BASE_URL } from "@/config";
import { useAuth } from "@/context/AuthProvider";

type SyncedLine = {
  time: number; // in ms
  line: string;
};

type Props = {
  trackId?: string;
  artist: string;
  title: string;
  albumName?: string;
  duration?: number;
  fallbackLyrics?: string;
  progress?: number; // in seconds
};

const LyricsView: React.FC<Props> = ({ trackId, artist, title, albumName, duration, fallbackLyrics, progress }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncedLyrics, setSyncedLyrics] = useState<SyncedLine[] | null>(null);
  const [plainLyrics, setPlainLyrics] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const lyricsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const parseLyrics = (rawLrc: string): SyncedLine[] => {
      return rawLrc
        .split("\n")
        .filter((line: string) => line.trim() !== "")
        .map((line: string): SyncedLine | null => {
          const match = line.match(/^\[(\d+):(\d+\.\d+)\](.*)$/);
          if (!match || match.length < 4) return null;
         
          const minutes = parseInt(match[1] || "0", 10);
          const seconds = parseFloat(match[2] || "0");
          const time = Math.round((minutes * 60 + seconds) * 1000); // in ms
          const lyric = (match[3] || "").trim();
         
          return { time, line: lyric };
        })
        .filter((line: SyncedLine | null): line is SyncedLine => line !== null);
    };

    const fetchLyrics = async () => {
      setLoading(true);
      try {
        if (!artist || !title) throw new Error("Missing artist or title");

        // 🔄 NEW: If we have trackId and token, try API endpoints first (like mobile does)
        if (trackId && token) {
          try {
            console.log(`🎵 Fetching lyrics from database for track: ${trackId}`);
            // 1. Try to get lyrics from database
            const dbRes = await fetch(
              `${API_BASE_URL}/lyrics/from-db?trackId=${trackId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                }
              }
            );

            if (dbRes.ok) {
              const dbData = await dbRes.json();
              console.log('✅ Found lyrics in database');
              
              if (dbData.lyrics) {
                // Check if lyrics are in LRC format
                if (dbData.lyrics.includes('[') && dbData.lyrics.includes(']')) {
                  setSyncedLyrics(parseLyrics(dbData.lyrics));
                } else {
                  setPlainLyrics(dbData.lyrics);
                }
                setLoading(false);
                return;
              }
            }

            console.log('🔍 Lyrics not in database, fetching externally...');
            // 2. If not in DB, fetch from external source and save to DB
            const fetchRes = await fetch(
              `${API_BASE_URL}/lyrics/fetch-external`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  trackId: trackId,
                  trackName: title,
                  artistName: artist,
                })
              }
            );

            if (fetchRes.ok) {
              const fetchData = await fetchRes.json();
              console.log('✅ Fetched and saved lyrics externally');
              
              if (fetchData.lyrics) {
                // Check if lyrics are in LRC format
                if (fetchData.lyrics.includes('[') && fetchData.lyrics.includes(']')) {
                  setSyncedLyrics(parseLyrics(fetchData.lyrics));
                } else {
                  setPlainLyrics(fetchData.lyrics);
                }
                setLoading(false);
                return;
              }
            }
          } catch (apiErr: any) {
            console.warn("API lyrics fetch failed, falling back to direct LRCLIB:", apiErr.message);
            // Continue to fallback below
          }
        }

        // 🔄 Fallback: Direct LRCLIB call (original behavior)
        console.log('📡 Fetching lyrics directly from LRCLIB...');
        const params = new URLSearchParams({
          artist_name: artist,
          track_name: title,
        });

        if (albumName) {
          params.append("album_name", albumName);
        }

        if (duration) {
          params.append("duration", Math.round(duration).toString());
        }

        const res = await fetch(`https://lrclib.net/api/get?${params.toString()}`);
        if (!res.ok) throw new Error("Track not found");

        const lyricsData = await res.json();

        if (lyricsData.syncedLyrics && typeof lyricsData.syncedLyrics === "string") {
          setSyncedLyrics(parseLyrics(lyricsData.syncedLyrics));
        } else if (lyricsData.plainLyrics) {
          setPlainLyrics(lyricsData.plainLyrics);
        } else {
          throw new Error("Lyrics not found in response");
        }
      } catch (err: any) {
        console.warn("Lyrics fetch failed:", err.message);
        if (fallbackLyrics) {
          setPlainLyrics(fallbackLyrics);
        } else {
          setError("No lyrics found.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLyrics();
  }, [trackId, artist, title, albumName, duration, fallbackLyrics, token]);

  useEffect(() => {
    if (!syncedLyrics || typeof progress !== "number") return;

    const currentMs = progress * 1000;

    for (let i = syncedLyrics.length - 1; i >= 0; i--) {
      const lyric = syncedLyrics[i];
      if (lyric && currentMs >= lyric.time) {
        if (i !== activeIndex) setActiveIndex(i);
        break;
      }
    }
  }, [progress, syncedLyrics, activeIndex]);

  useEffect(() => {
    if (lyricsContainerRef.current && activeIndex !== -1) {
      const activeEl = document.getElementById(`lyric-line-${activeIndex}`);
      if (activeEl && activeEl.scrollIntoView) {
        activeEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [activeIndex]);

  return (
    <Box
      sx={{
        padding: "20px",
        color: "white",
        height: "100%",
        overflowY: "auto",
        textAlign: "center",
      }}
      ref={lyricsContainerRef}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
         {title}
      </Typography>

      {loading && <CircularProgress color="inherit" />}

      {!loading && syncedLyrics && (
        <Box sx={{ mt: 2 }}>
          {syncedLyrics.map((line, index) => (
            <Typography
              key={index}
              id={`lyric-line-${index}`}
              variant="body2"
              sx={{
                lineHeight: 1.8,
                color: index === activeIndex ? "#E4FF1A" : "Yellow",
                fontWeight: index === activeIndex ? "bold" : "normal",
              }}
            >
              {line.line}
            </Typography>
          ))}
        </Box>
      )}

      {!loading && !syncedLyrics && plainLyrics && (
        <Typography
          variant="body2"
          sx={{ mt: 2, whiteSpace: "pre-wrap", lineHeight: 1.8 }}
        >
          {plainLyrics}
        </Typography>
      )}

      {!loading && error && (
        <Typography
          variant="body2"
          sx={{ mt: 2, fontStyle: "italic", opacity: 0.7 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default LyricsView;






